import React, {useContext, useEffect, useMemo, useRef} from "react";
import classnames from "classnames";
import UserPhoneHomepageProductCard from "../../../product-card";
import {Fade, Tooltip} from "@mui/material";
import UserPhoneHomepageProductSlider from "../../../product-slider";
import {
    UserHomepageComponentTypes,
    UserHomepageControlPanelEvents,
    UserHomepageEventEmitter,
    UserHomepageProductTypes,
    UserHomepageViewMode
} from "../../../../../../models";
import {UserHomepageControllerContext, UserHomepageStateContext} from "../../../../../../index";
import {preventEventPropagation} from "../../../../../../../../../core/services/utils/utils";

const UserPhoneHomepageProducts = ({component, tabId, hideHeader = false}) => {
    const {viewMode} = useContext(UserHomepageStateContext);
    const controller = useContext(UserHomepageControllerContext);
    const type = useMemo(() => {
        switch (component?.type?.id) {
            case UserHomepageComponentTypes?.smallProduct:
                return UserHomepageProductTypes.small;
            case UserHomepageComponentTypes?.mediumProduct:
            default:
                return UserHomepageProductTypes.medium;
            case UserHomepageComponentTypes?.largeProduct:
                return UserHomepageProductTypes.large;
        }
    }, [component])

    const products = useMemo(() => component?.content?.parts ?? [], [component]);
    const showContainer = useMemo(() => type === UserHomepageProductTypes.small, [type]);
    /**@type {React.MutableRefObject<import('react-owl-carousel/umd/OwlCarousel.d.ts').default>}*/
    const carouselRef = useRef();

    /** @type{boolean} */
    const showMoreButton = useMemo(() =>
            (!!component.searchQuery && viewMode === UserHomepageViewMode.preview) ||
            (viewMode === UserHomepageViewMode.edit),
        [viewMode, component?.searchQuery])
    /** @type{boolean}*/
    const moreButtonInvisible = useMemo(() =>
            viewMode === UserHomepageViewMode.edit && !component.nexusSearchId,
        [viewMode, component?.nexusSearchId])

    /**
     * With each change in the controller:
     * - attaches the event listeners for content selection for focusing purposes.
     */
    useEffect(() => {
        controller.on(UserHomepageControlPanelEvents.onContentSelected, focusComponentContent);
        return () => {
            controller.off(UserHomepageControlPanelEvents.onContentSelected, focusComponentContent);
        }
    }, [component, controller])


    /**
     * Uses the owl carousel api to bring the selected content into view.
     *
     * @param {number} tabId
     * @param {number} componentId
     * @param {number} contentId
     */
    const focusComponentContent = (tabId, componentId, contentId) => {
        // only componentId is checked since each component has a unique id in the server.
        if (componentId !== component.id)
            return;
        switch (component.type?.id) {
            case UserHomepageComponentTypes.smallProduct:
                const contentIndex = component.content?.parts?.findIndex(e => e.id === contentId);
                if (contentIndex === -1)
                    return;
                try {
                    carouselRef.current.to(contentIndex, 250);
                } catch (e) {
                }
                break;
            default:
                break;
        }
    }

    /**
     * Invokes the appropriate controller callback for the click of the products more button.
     */
    const onMoreButtonClicked = () => {
        controller.selectComponentSearchButton(tabId, component.id, UserHomepageEventEmitter.view);
    }

    /**
     * Invokes the appropriate controller callback for the selection of a product in a slider.
     * @param {any} product
     */
    const onProductSelected = (product) => {
        controller.selectContent(tabId, component.id, product.id, UserHomepageEventEmitter.view);
    }

    /**
     * Invokes the appropriate controller callback for the click of a product.
     * @param {any} product
     */
    const onProductClicked = (product) => {
        controller.clickContent(tabId, component.id, product.id, UserHomepageEventEmitter.view);
    }

    /**
     * Invokes the appropriate controller callback for the click of the tag of a tag.
     *
     * @param {any} product
     * @param {any} tag
     */
    const onTagClicked = (product, tag) => {
        controller.selectProductTag(tabId, component.id, product.id, tag.id, UserHomepageEventEmitter.view);
    }


    /**@type {JSX.Element}*/
    const moreButton = useMemo(() => (
        <button
            onClick={onMoreButtonClicked}
            className={classnames('user-phone-homepage-more-button', {'not-visible': moreButtonInvisible})}>
            More
        </button>
    ), [moreButtonInvisible])

    /**
     * Renders the container of the product based on the type of the product.
     *
     * * if small: renders the slider.
     * @return {JSX.Element}
     */
    const container = useMemo(() => {
        switch (type) {
            case UserHomepageProductTypes.small:
                return <UserPhoneHomepageProductSlider
                    type={type}
                    products={products}
                    onSelectionChanged={onProductSelected}
                    onProductClicked={onProductClicked}
                    onTagClicked={onTagClicked}
                    ref={carouselRef}
                />
            default:
                return (
                    <div className={classnames('d-flex flex-column align-items-center w-100')}>
                        {
                            products.map((product) => (
                                <UserPhoneHomepageProductCard
                                    key={product.id}
                                    data={product}
                                    type={type}
                                    onProductClicked={onProductClicked}
                                    onTagClicked={onTagClicked}
                                />
                            ))
                        }
                    </div>
                );

        }
    }, [products, type])


    return (
        <>
            <div className={classnames(
                'user-phone-homepage-products',
                {
                    'with-container': showContainer,
                }
            )}>
                {
                    !hideHeader &&
                    <div className={'component-title'}>
                        <p>
                            {component?.title ?? ''}
                        </p>
                        <Fade in={showMoreButton}>
                            {
                                moreButtonInvisible
                                    ? <Tooltip
                                        title={'No search query or search type specified'}
                                    >
                                        {moreButton}
                                    </Tooltip>
                                    : moreButton
                            }
                        </Fade>
                    </div>
                }
                <div
                    className={classnames({'products-container': showContainer})}
                    onClick={preventEventPropagation}
                >
                    {container}
                </div>
            </div>
        </>
    );
}

export default UserPhoneHomepageProducts;
